import { useCallback, useEffect } from 'react';
import { useSetImmerAtom } from 'jotai-immer';
import { useImmerAtom } from 'jotai-immer';
import {
    procErrorsTableStateAtom,
    procErrorsTableViewAtom,
    procErrorsTableStatsAtom,
    procErrorsTableFiltersAtom,
    procErrorsDataAtom,
    ProcessingErrorItem,
} from './processingerrors_atoms';
import { processingErrorsColumnSpec, processingErrorsDefaultView } from './ProcessingErrorsTableSpec';
import { tblSpecLoadView } from '../../../../core/table/tableViewsRepo';
import { tableViewVM, tableStateVM, tableStatsVM } from '../../../../core/table/table_view_models';
import datamonitorRepo from '../../data/datamonitorRepo';

type UseProcErrorsTableInitReturn = {
    init: boolean;
    loadProcessingErrors: (ce: string) => void;
};

export const useProcessingErrorsTableInit = (isVerity = false): UseProcErrorsTableInitReturn => {
    const [{ init }, setState] = useImmerAtom(procErrorsTableStateAtom);
    const setView = useSetImmerAtom(procErrorsTableViewAtom);
    const setStats = useSetImmerAtom(procErrorsTableStatsAtom);
    const setFilter = useSetImmerAtom(procErrorsTableFiltersAtom);
    const setData = useSetImmerAtom(procErrorsDataAtom);

    useEffect(() => {
        if (init) {
            const colSpec = processingErrorsColumnSpec(isVerity);
            const defaultView = processingErrorsDefaultView(isVerity);
            const view = tblSpecLoadView(defaultView.key, [defaultView], colSpec);
            setView(() => view || tableViewVM());
            setFilter(() => ({}));
            setStats(() => tableStatsVM());
            setState(draft => {
                draft.views = [defaultView];
                draft.currentViewId = defaultView.key;
                draft.init = false;
            });
        }
    }, [init, isVerity, setState, setView, setFilter, setStats]);

    const loadProcessingErrors = useCallback(
        async (ce: string): Promise<void> => {
            // Reset filters and stats before fetching
            setFilter(() => ({}));
            setStats(() => tableStatsVM());
            setState(draft => {
                draft.waiting = true;
            });
            try {
                const items = await datamonitorRepo.getProcessingErrors(ce, isVerity);
                setData(draft => {
                    draft.dataItems = items as ProcessingErrorItem[];
                    draft.items = [];
                });
            } catch {
                setData(() => ({ items: [], dataItems: [] }));
            } finally {
                setState(draft => {
                    draft.waiting = false;
                });
            }
        },
        [isVerity, setState, setData, setFilter, setStats],
    );

    // Cleanup on unmount
    useEffect(() => {
        return (): void => {
            setState(() => tableStateVM());
            setView(() => tableViewVM());
            setFilter(() => ({}));
            setStats(() => tableStatsVM());
            setData(() => ({ items: [], dataItems: [] }));
        };
    }, [setState, setView, setFilter, setStats, setData]);

    return { init, loadProcessingErrors };
};
